
import { Link } from 'react-router-dom';
import Header from '../../../components/feature/Header';
import Footer from '../../../components/feature/Footer';
import { useEffect } from 'react';

export default function WaterproofingPage() {
  useEffect(() => {
    // Update page title and meta tags
    document.title = 'Waterproofing Cape Town | Roof Waterproofing & Basement Sealing | Professional Contractors';
    
    // Update meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Professional waterproofing services in Cape Town. Roof waterproofing, basement sealing, foundation protection. Prevent moisture damage with expert contractors. Free quotes available.');
    }

    // Update meta keywords
    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', 'waterproofing Cape Town, roof waterproofing, basement sealing, moisture protection, waterproofing contractors');
    }

    // Update canonical URL
    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) {
      canonical.setAttribute('href', `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/services/waterproofing`);
    }

    // Add Schema.org JSON-LD
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "Service",
      "name": "Waterproofing Services Cape Town",
      "description": "Professional waterproofing services including roof waterproofing, basement sealing, and foundation protection for residential and commercial properties.",
      "url": `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/services/waterproofing`,
      "provider": {
        "@type": "LocalBusiness",
        "name": "Cape Town Professional Painters",
        "areaServed": "Cape Town, Western Cape, South Africa"
      }
    });
    document.head.appendChild(script);

    return () => {
      const existingScript = document.querySelector('script[type="application/ld+json"]');
      if (existingScript && existingScript.textContent?.includes('Waterproofing Services Cape Town')) {
        existingScript.remove();
      }
    };
  }, []);

  const waterproofingServices = [
    {
      title: 'Roof Waterproofing',
      description: 'Complete roof waterproofing solutions to prevent leaks and water damage.',
      features: ['Membrane installation', 'Leak detection', 'Drainage systems', 'Maintenance plans']
    },
    {
      title: 'Basement Sealing',
      description: 'Protect below-grade spaces from moisture infiltration and flooding.',
      features: ['Interior sealing', 'Exterior barriers', 'Sump pump systems', 'Humidity control']
    },
    {
      title: 'Foundation Waterproofing',
      description: 'Comprehensive foundation protection against water damage and structural issues.',
      features: ['Crack injection', 'Membrane systems', 'Drainage solutions', 'Structural repairs']
    },
    {
      title: 'Deck & Balcony Waterproofing',
      description: 'Specialized waterproofing for outdoor living spaces and structural elements.',
      features: ['Surface preparation', 'Membrane application', 'Drainage design', 'Finish coatings']
    }
  ];

  const waterproofingSolutions = [
    {
      problem: 'Roof Leaks',
      solution: 'Advanced membrane systems and proper drainage to eliminate water infiltration.',
      icon: 'ri-home-8-line'
    },
    {
      problem: 'Basement Flooding',
      solution: 'Interior and exterior sealing with sump pump systems for complete protection.',
      icon: 'ri-building-line'
    },
    {
      problem: 'Foundation Cracks',
      solution: 'Professional crack injection and structural waterproofing solutions.',
      icon: 'ri-hammer-line'
    },
    {
      problem: 'Moisture Damage',
      solution: 'Comprehensive moisture barriers and ventilation systems for long-term protection.',
      icon: 'ri-droplet-line'
    }
  ];

  return (
    <div className="min-h-screen">
      <Header />
      
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-gray-900 to-black text-white py-20">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/3e3389fb94b8386cb0daa1b4a942df54.jpg"
            alt="Professional waterproofing services in Cape Town"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-black/70"></div>
        </div>
        <div className="container mx-auto px-6 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl lg:text-6xl font-bold leading-tight mb-6">
              Professional <span className="text-yellow-400">Waterproofing</span> Services
            </h1>
            <p className="text-xl text-gray-300 leading-relaxed mb-8">
              Protect your property from moisture damage with expert waterproofing services in Cape Town. Our contractors provide comprehensive solutions for roofs, basements, and foundations.
            </p>
            <div className="flex gap-4">
              <Link
                to="/contact"
                className="bg-yellow-400 text-black px-8 py-4 rounded-lg text-lg font-semibold hover:bg-yellow-500 transition-colors whitespace-nowrap cursor-pointer"
              >
                Get Quote
              </Link>
              <Link
                to="/services"
                className="bg-gray-100 text-black px-8 py-4 rounded-lg text-lg font-semibold hover:bg-gray-200 transition-colors whitespace-nowrap cursor-pointer"
              >
                Services
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Waterproofing Services */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-20">
            <h2 className="text-4xl md:text-5xl font-bold text-black mb-6">
              Comprehensive Waterproofing Solutions
            </h2>
            <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
              From roof leaks to basement flooding, our network of waterproofing specialists provides complete moisture protection for residential and commercial properties.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            {waterproofingServices.map((service, index) => (
              <div key={index} className="bg-gray-50 rounded-2xl p-8 hover:shadow-lg transition-shadow border border-gray-100">
                <h3 className="text-2xl font-bold text-black mb-4">{service.title}</h3>
                <p className="text-gray-600 mb-6 leading-relaxed">{service.description}</p>
                
                <div className="grid grid-cols-2 gap-3">
                  {service.features.map((feature, idx) => (
                    <div key={idx} className="flex items-center">
                      <i className="ri-check-double-line text-green-500 mr-2 text-sm"></i>
                      <span className="text-gray-700 text-sm">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Waterproofing Solutions */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-black mb-6">
              Common Water Problems & Solutions
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Our contractors diagnose and solve the most common waterproofing challenges in Cape Town properties
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {waterproofingSolutions.map((item, index) => (
              <div key={index} className="bg-white rounded-xl p-6 text-center hover:shadow-lg transition-shadow border border-gray-100">
                <div className="w-16 h-16 flex items-center justify-center mx-auto mb-6" style={{backgroundColor: '#ffff00'}}>
                  <i className={`${item.icon} text-black text-2xl`}></i>
                </div>
                <h3 className="text-lg font-bold text-black mb-3">{item.problem}</h3>
                <p className="text-gray-600 text-sm">{item.solution}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Cape Town Climate Challenges */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-black mb-6">
              Cape Town Climate Challenges
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Our waterproofing solutions are specifically designed for Cape Town's unique coastal climate
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-20 h-20 bg-yellow-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <i className="ri-water-percent-line text-3xl text-yellow-600"></i>
              </div>
              <h3 className="text-xl font-bold text-black mb-4">Coastal Moisture</h3>
              <p className="text-gray-600">High humidity and salt air require specialized waterproofing materials and techniques for lasting protection.</p>
            </div>

            <div className="text-center">
              <div className="w-20 h-20 bg-yellow-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <i className="ri-windy-line text-3xl text-yellow-600"></i>
              </div>
              <h3 className="text-xl font-bold text-black mb-4">Winter Storms</h3>
              <p className="text-gray-600">Intense winter rainfall and wind-driven rain demand robust waterproofing systems and proper drainage.</p>
            </div>

            <div className="text-center">
              <div className="w-20 h-20 bg-yellow-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <i className="ri-temp-cold-line text-3xl text-yellow-600"></i>
              </div>
              <h3 className="text-xl font-bold text-black mb-4">Temperature Fluctuations</h3>
              <p className="text-gray-600">Variable temperatures require flexible waterproofing materials that expand and contract without failure.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-black mb-6">
              Our Waterproofing Process
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Systematic approach ensuring complete moisture protection and long-term reliability
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-8">
            {[
              {
                step: '01',
                title: 'Moisture Assessment',
                description: 'Comprehensive evaluation of water sources, damage extent, and structural conditions.'
              },
              {
                step: '02',
                title: 'System Design',
                description: 'Custom waterproofing solution design based on property needs and local conditions.'
              },
              {
                step: '03',
                title: 'Surface Preparation',
                description: 'Thorough cleaning, repair, and preparation of all surfaces for optimal adhesion.'
              },
              {
                step: '04',
                title: 'Waterproof Installation',
                description: 'Professional application of membranes, sealants, and drainage systems.'
              },
              {
                step: '05',
                title: 'Testing & Warranty',
                description: 'Comprehensive testing and documentation with full warranty coverage.'
              }
            ].map((process, index) => (
              <div key={index} className="text-center">
                <div className="w-16 h-16 flex items-center justify-center mx-auto mb-6" style={{backgroundColor: '#ffff00'}}>
                  {index === 0 && <i className="ri-drop-line text-black text-2xl"></i>}
                  {index === 1 && <i className="ri-shield-check-line text-black text-2xl"></i>}
                  {index === 2 && <i className="ri-tools-line text-black text-2xl"></i>}
                  {index > 2 && <span className="text-black text-lg font-bold">{process.step}</span>}
                </div>
                <h4 className="text-lg font-bold text-black mb-4">{process.title}</h4>
                <p className="text-gray-600 leading-relaxed text-sm">{process.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-black mb-6">
              Benefits of Professional Waterproofing
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-20 h-20 bg-yellow-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <i className="ri-shield-check-line text-3xl text-yellow-600"></i>
              </div>
              <h3 className="text-xl font-bold text-black mb-4">Property Protection</h3>
              <p className="text-gray-600">Prevent costly water damage, mold growth, and structural deterioration with professional waterproofing.</p>
            </div>

            <div className="text-center">
              <div className="w-20 h-20 bg-yellow-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <i className="ri-home-heart-line text-3xl text-yellow-600"></i>
              </div>
              <h3 className="text-xl font-bold text-black mb-4">Increased Value</h3>
              <p className="text-gray-600">Professional waterproofing increases property value and provides peace of mind for buyers and sellers.</p>
            </div>

            <div className="text-center">
              <div className="w-20 h-20 bg-yellow-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <i className="ri-heart-line text-3xl text-yellow-600"></i>
              </div>
              <h3 className="text-xl font-bold text-black mb-4">Health &amp; Comfort</h3>
              <p className="text-gray-600">Eliminate moisture-related health issues and create a comfortable, dry living environment.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Related Services */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-black mb-6">
              Related Painting Services
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Complete your property transformation with our comprehensive painting solutions
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <Link
              to="/services/roof-painting"
              className="bg-white rounded-xl p-6 text-center hover:shadow-lg transition-all cursor-pointer group border border-gray-100"
            >
              <i className="ri-home-8-line text-3xl text-yellow-600 mb-4 group-hover:scale-110 transition-transform"></i>
              <h3 className="font-bold text-black group-hover:text-yellow-600 transition-colors">
                Roof Painting
              </h3>
              <p className="text-gray-600 text-sm mt-2">Protective roof coatings</p>
            </Link>

            <Link
              to="/services/exterior-painting"
              className="bg-white rounded-xl p-6 text-center hover:shadow-lg transition-all cursor-pointer group border border-gray-100"
            >
              <i className="ri-building-2-line text-3xl text-yellow-600 mb-4 group-hover:scale-110 transition-transform"></i>
              <h3 className="font-bold text-black group-hover:text-yellow-600 transition-colors">
                Exterior Painting
              </h3>
              <p className="text-gray-600 text-sm mt-2">Weather-resistant coatings</p>
            </Link>

            <Link
              to="/services/commercial-painting"
              className="bg-white rounded-xl p-6 text-center hover:shadow-lg transition-all cursor-pointer group border border-gray-100"
            >
              <i className="ri-community-line text-3xl text-yellow-600 mb-4 group-hover:scale-110 transition-transform"></i>
              <h3 className="font-bold text-black group-hover:text-yellow-600 transition-colors">
                Commercial Painting
              </h3>
              <p className="text-gray-600 text-sm mt-2">Business waterproofing</p>
            </Link>

            <Link
              to="/services/interior-painting"
              className="bg-white rounded-xl p-6 text-center hover:shadow-lg transition-all cursor-pointer group border border-gray-100"
            >
              <i className="ri-home-4-line text-3xl text-yellow-600 mb-4 group-hover:scale-110 transition-transform"></i>
              <h3 className="font-bold text-black group-hover:text-yellow-600 transition-colors">
                Interior Painting
              </h3>
              <p className="text-gray-600 text-sm mt-2">Moisture-resistant interiors</p>
            </Link>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-24 bg-gradient-to-r from-yellow-400 to-yellow-500">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-black mb-8">
            Ready to Protect Your Property from Water Damage?
          </h2>
          <p className="text-xl text-black/80 mb-12 max-w-4xl mx-auto leading-relaxed">
            Connect with Cape Town's most trusted waterproofing contractors through our platform. Get up to 5 quotes and choose the perfect contractor for your waterproofing project.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="bg-black text-white px-10 py-4 rounded-lg text-xl font-bold hover:bg-gray-800 transition-colors whitespace-nowrap cursor-pointer"
            >
              Get Quote
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
